import { Component, OnInit, Input, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ObjectId } from 'bson';
import { filter, Subscription, take } from 'rxjs';

import {
  CategorySourceTypes,
  ILegacyList,
  ItemStates,
  TableCellData,
  TableColumnWidths,
} from '@site-mate/dashpivot-shared-library';

import { IDryList, TemplateListsService } from 'app/apps/template-lists.service';
import { TemplatesService } from 'app/apps/templates.service';
import { FieldWeb } from 'app/shared/model/item.model';
import { ToastrService } from 'app/shared/service/toastr.service';
import { environment } from 'environments/environment';

import { AppTableColumnService } from '../../app-table-column.service';
import { ITemplateTableCell } from '../template-table-cell.interface';

@Component({
  styleUrls: ['template-table-list-cell.component.scss'],
  templateUrl: 'template-table-list-cell.component.html',
})
export class TemplateTableListCellComponent implements ITemplateTableCell, OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly templateListsService = inject(TemplateListsService);
  private readonly toastrService = inject(ToastrService);
  private readonly appTableColumnService = inject(AppTableColumnService);
  private readonly templatesService = inject(TemplatesService);

  private readonly fb = inject(FormBuilder);

  @Input() index: number;
  @Input() cellData: TableCellData;
  @Input() table: FieldWeb;
  @Input() isOrganisationTemplate: boolean;
  @Input() isDefaultTable: boolean;
  readonly TableColumnWidths = TableColumnWidths;
  readonly CategorySourceTypes = CategorySourceTypes;
  readonly enableListPropertyTableCells = environment.featureToggles.listPropertyTableCell;
  public listCellForm: FormGroup;

  editingListItem: boolean;
  loading: boolean;
  availableLists = new Map<IDryList['_id'], IDryList>();
  unavailableLists = new Set<IDryList['_id']>();
  listReferenceDeleted = false;

  ngOnInit(): void {
    this.setupForm();
    this.getAvailableLists();
    this.setupLoadingState();
    this.handleMissingList();
  }

  private setupForm() {
    this.cellData.listSource = this.cellData.listSource || CategorySourceTypes.List;
    this.listCellForm = this.fb.group({
      listSource: [this.cellData.listSource],
      reference: [this.cellData.reference || null],
      autoDeployChildList: [this.cellData.autoDeployChildList],
    });

    this.listCellForm
      .get('listSource')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.onListSourceChange.bind(this));

    this.listCellForm
      .get('autoDeployChildList')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((autoDeployChildList) => {
        this.cellData.autoDeployChildList = autoDeployChildList;
      });

    this.listCellForm
      .get('reference')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((reference) => {
        this.cellData.reference = reference;
        if (reference && this.availableLists.has(reference)) {
          this.listReferenceDeleted = false;
        }
      });
  }

  private getAvailableLists(): Subscription {
    return this.templateListsService.availableLists$.pipe(filter(Boolean), take(1)).subscribe((lists) => {
      lists.forEach((list) => this.availableLists.set(list._id, list));
    });
  }

  setupLoadingState(): Subscription {
    return this.templateListsService.listIsLoading$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(() => this.isListUnavailable()),
      )
      .subscribe((listIsLoadingMap) => {
        this.loading = listIsLoadingMap.get(this.cellData.reference);
      });
  }

  private handleMissingList() {
    if (this.isListUnavailable()) {
      this.templateListsService
        .getListData(this.cellData.reference)
        .pipe(filter(Boolean), take(1))
        .subscribe({
          next: (list) => {
            const { _id, name } = list;
            this.unavailableLists.add(this.cellData.reference);
            this.availableLists.set(_id, { _id, name });
            this.updateListIfWasDeleted(list);
          },
          error: (err) => {
            if (err.status === 401) {
              this.unavailableLists.add(this.cellData.reference);
            }
          },
        });
    }
  }

  private updateListIfWasDeleted(list: ILegacyList) {
    if (list.state === ItemStates.Deleted) {
      this.availableLists.delete(list._id);
      this.listReferenceDeleted = true;
      this.listCellForm.get('reference').reset();
      this.toastrService.errorByKey('listInTemplateIsDeleted', {
        toastClass: 'ngx-toastr toast-long-message',
      });
    }
  }

  private isListUnavailable() {
    return this.cellData.reference && !this.availableLists.has(this.cellData.reference);
  }

  onListSourceChange(sourceType: CategorySourceTypes) {
    this.cellData.listSource = sourceType;

    if (sourceType === CategorySourceTypes.List) {
      delete this.cellData.manualListItems;
    } else if (sourceType === CategorySourceTypes.Manual) {
      this.listCellForm.get('reference').reset();
      this.cellData.content = [];
    }
  }

  addListItem(event: HTMLInputElement) {
    if (event.value) {
      this.cellData.manualListItems = this.cellData.manualListItems || [];

      this.cellData.manualListItems.push({
        id: String(new ObjectId()),
        value: event.value,
        style: this.cellData.defaultColor,
      });
    } else {
      this.toastrService.warnByKey('inputTextBeforeAdd');
    }
    event.value = '';
    event.focus();
  }

  removeListItem(id: string) {
    this.cellData.manualListItems = this.cellData.manualListItems.filter((i) => i.id !== id);
  }

  onEditListItem(item) {
    this.editingListItem = item;
  }

  onAddListPropertyColumn() {
    const { reference, _id } = this.cellData;
    const content = { columnId: _id, listId: reference };

    this.appTableColumnService.addListPropertyColumn(this.table, this.index + 1, content);
    this.templatesService.onTemplateUpdateChange();
  }
}
